import { useIntlayer } from 'react-intlayer';

import { FadeIn } from '~/components/ui/fade-in';
import { ImageBackground } from '~/components/ui/image-background';
import { ProductCTASection } from '~/components/ui/ProductCTASection';
import { ProductMainPhraseSection } from '~/components/ui/ProductMainPhraseSection';
import { Typography } from '~/components/ui/typography';

export const AboutPageContent = () => {
  const { intro, mainPhrase, about, team, principles, cta } =
    useIntlayer('about');

  const principleItems = [principles.items.p01, principles.items.p02, principles.items.p03];

  return (
    <div className="bg-black min-h-full w-full">
      {/* ── Intro section ───────────────────────────── */}
      <section className="w-full px-4 md:px-20 pt-[130px] md:pt-[200px]">
        <div className="flex flex-col gap-2.5 md:gap-5 max-w-[981px]">
          <FadeIn>
            <Typography variant="label">{intro.label}</Typography>
          </FadeIn>
          <FadeIn delay={0.15}>
            <p className="font-manrope font-medium md:font-normal text-xl md:text-[30px] leading-normal tracking-[0.24px] md:tracking-normal text-white">
              {intro.description}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── Hero image ──────────────────────────────── */}
      <section className="relative w-full mt-10 md:mt-[60px] aspect-[375/536] md:aspect-auto md:h-[810px]">
        <ImageBackground imageUrl="/assets/images/about1.jpg" />
      </section>

      {/* ── Main phrase ─────────────────────────────── */}
      <ProductMainPhraseSection text={mainPhrase.line1.value} />

      {/* ── About description (light bg) ────────────── */}
      <section className="w-full bg-neutral-1 px-4 md:px-20 py-10 md:py-[160px]">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5 md:gap-0">
          <FadeIn>
            <Typography
              variant="label"
              className="text-black shrink-0"
            >
              {about.label}
            </Typography>
          </FadeIn>

          <div className="flex flex-col gap-10 md:gap-[60px] md:max-w-[737px] md:pr-[110px]">
            <FadeIn delay={0.1}>
              <p className="font-manrope font-normal text-[30px] md:text-4xl leading-normal text-black">
                {about.heading}
              </p>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="flex flex-col gap-5 max-w-[628px]">
                <Typography variant="base" className="text-black">
                  {about.p1}
                </Typography>
                <Typography variant="base" className="text-black">
                  {about.p2}
                </Typography>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── Our team ────────────────────────────────── */}
      <section className="w-full pt-[60px] md:pt-[140px] text-center text-white">
        <div className="flex flex-col gap-5 items-start md:items-center px-4 md:px-20">
          <FadeIn>
            <Typography variant="label">{team.label}</Typography>
          </FadeIn>
          <FadeIn delay={0.15}>
            <p className="font-manrope font-medium text-xl md:text-[40px] tracking-[0.24px] md:tracking-normal text-white">
              {team.title}
            </p>
          </FadeIn>
        </div>

        <div className="flex flex-col md:flex-row items-stretch w-full mt-[60px] md:mt-[120px]">
          {[
            { col: team.col1, id: 'veterans', border: 'md:border-r' },
            { col: team.col2, id: 'experts', border: '' },
            { col: team.col3, id: 'doers', border: 'md:border-l' },
          ].map(({ col, id, border }) => (
            <div
              key={id}
              className={`flex-1 flex flex-col gap-4 items-center justify-center px-4 md:px-20 py-12 md:py-[24px] min-h-0 md:min-h-[320px] border-t border-graphite-primary ${border}`}
            >
              <Typography
                as="p"
                className="font-manrope font-normal text-[40px] md:text-5xl text-white"
              >
                {col.title}
              </Typography>
              <Typography variant="label">{col.subtitle}</Typography>
            </div>
          ))}
        </div>
      </section>

      {/* ── Team image ──────────────────────────────── */}
      <section className="relative w-full h-[440px] md:h-[873px]">
        <ImageBackground imageUrl="/assets/images/about2.jpg" />
      </section>

      {/* ── Our principles ──────────────────────────── */}
      <section className="w-full bg-graphite-primary px-4 md:px-20 py-10 md:py-[120px]">
        <div className="flex flex-col gap-5 text-white">
          <FadeIn>
            <Typography variant="label">{principles.label}</Typography>
          </FadeIn>
          <FadeIn delay={0.15}>
            <p className="font-manrope font-medium text-xl md:text-[40px] text-white">
              {principles.title}
            </p>
          </FadeIn>
        </div>

        <div className="flex flex-col gap-12 md:gap-20 mt-8 md:mt-[24px]">
          {principleItems.map((item, index) => (
            <div key={item.number} className="flex flex-col gap-6">
              <div className="h-px w-full bg-white/20" />
              <FadeIn delay={index * 0.1}>
                <div className="flex flex-col gap-6 md:gap-0 md:flex-row md:items-start md:justify-between md:pr-[218px] text-white">
                  <div className="flex flex-col md:flex-row gap-3 md:gap-6 items-start w-full md:w-[302px] shrink-0">
                    <Typography variant="label">
                      {item.number}
                    </Typography>
                    <Typography
                      as="p"
                      className="font-manrope font-medium text-lg leading-[1.4] md:w-[193px]"
                    >
                      {item.title}
                    </Typography>
                  </div>
                  <div className="md:w-[530px]">
                    <Typography variant="base">
                      {item.description}
                    </Typography>
                  </div>
                </div>
              </FadeIn>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────── */}
      <ProductCTASection
        title={cta.label}
        heading={cta.heading}
        button1Text={cta.button}
        backgroundImage="/assets/images/chorna_cta.jpg"
        imageClassName="flip-horizontal"
      />
    </div>
  );
};
